import type { Option } from '@branchjs/neo-components';
import { createSelector } from 'reselect';

import { INIT_WRAPPER_STATE } from 'client/js/redux/constants/ReduxAsyncActions.constants';
import type { WrappedReducer } from 'client/js/redux/types/ReduxAsyncActions.types';
import type { RootState } from '../../types/redux.types';
import { selectEnabledAdPartnerOptions } from '../AdPartner/AdPartner.selectors';
import { INIT_STATE } from './CostFileTemplate.constants';
import type { CostFileTemplateState } from './CostFileTemplate.types';

type PartialRootState = {
  costFileTemplate: WrappedReducer<CostFileTemplateState>;
};

export const selectCostFileTemplateState = (state: PartialRootState): WrappedReducer<CostFileTemplateState> =>
  state.costFileTemplate || { ...INIT_WRAPPER_STATE, ...INIT_STATE };

export const selectIsLoading: (state: PartialRootState) => boolean = createSelector(
  selectCostFileTemplateState,
  (state) => state && state.loading
);

const selectUsedAdPartnersLookup: (state: PartialRootState) => Map<string, string> = createSelector(
  selectCostFileTemplateState,
  (state) => state.adPartners
);

export type AdPartnerIngestorOption = Option & { isDisabled: boolean; templateId?: string };

export const selectAdPartnerIngestorOptions: (state: RootState) => AdPartnerIngestorOption[] = createSelector(
  selectEnabledAdPartnerOptions,
  selectUsedAdPartnersLookup,
  (adPartnerOptions, usedAdPartners) =>
    adPartnerOptions
      .map((partner) => ({
        ...partner,
        isDisabled: !usedAdPartners.has(partner.value),
        templateId: usedAdPartners.get(partner.value),
      }))
      .sort((a, b) => {
        if (a.isDisabled !== b.isDisabled) {
          return a.isDisabled ? 1 : -1;
        }
        return a.label.localeCompare(b.label);
      })
);

export const selectAdPartnerTemplateOptions: (state: RootState) => (Option & { isDisabled: boolean })[] = createSelector(
  selectEnabledAdPartnerOptions,
  selectUsedAdPartnersLookup,
  (adPartnerOptions, usedAdPartners) =>
    adPartnerOptions
      .map((partner) => ({
        ...partner,
        isDisabled: usedAdPartners.has(partner.value),
      }))
      .sort((a, b) => {
        if (a.isDisabled !== b.isDisabled) {
          return a.isDisabled ? 1 : -1;
        }
        return a.label.localeCompare(b.label);
      })
);
